import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, ClipboardPaste, Copy, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { API_BASE } from '../../config/api';
import { parsePeople, isEmail } from '../../lib/parsePeople';

const API = `${API_BASE}/api`;
const ROLES = ['learner', 'instructor', 'admin', 'auditor', 'super_admin'];
const inp = 'h-9 w-full bg-zinc-950 border border-zinc-700 rounded-md text-sm text-white px-2 focus:outline-none focus:border-navigate-gold';
const blank = () => ({ full_name: '', email: '', role: '', cohort_id: '' });

export default function BulkAddUsersDialog({ open, onOpenChange, token, cohorts, onDone }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([blank()]);
  const [paste, setPaste] = useState('');
  const [batch, setBatch] = useState({ role: 'learner', cohort_id: '', programme_ids: [] });
  const [programmes, setProgrammes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRows([blank()]); setPaste(''); setResult(null); setBatch({ role: 'learner', cohort_id: '', programme_ids: [] });
    axios.get(`${API}/programmes/admin/all`, { headers })
      .then((r) => setProgrammes(Array.isArray(r.data) ? r.data : (r.data.programmes || []))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cohortByName = (n) => cohorts.find((c) => c.name.toLowerCase() === (n || '').trim().toLowerCase())?.cohort_id || '';
  const addParsed = (list) => {
    if (!list.length) { toast.error('No people found. Use one person per line: Name, email'); return; }
    const mapped = list.map((p) => ({ full_name: p.full_name, email: p.email, role: ROLES.includes(p.role) ? p.role : '', cohort_id: cohortByName(p.cohort) }));
    const unknown = [...new Set(list.filter((p) => p.cohort && !cohortByName(p.cohort)).map((p) => p.cohort))];
    if (unknown.length) toast.warning(`Cohort not found: ${unknown.join(', ')}. Those people will use the batch cohort — create the cohort first if needed.`);
    setRows((prev) => [...prev.filter((r) => r.email || r.full_name), ...mapped]);
    toast.success(`Added ${mapped.length} ${mapped.length === 1 ? 'person' : 'people'} to the list`);
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    addParsed(parsePeople(await f.text()));
    e.target.value = '';
  };
  const setRow = (i, k, v) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const filled = rows.filter((r) => r.email.trim());
  const invalid = filled.filter((r) => !isEmail(r.email.trim())).length;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...batch, cohort_id: batch.cohort_id || null, send_invite: true,
        rows: filled.map((r) => ({ full_name: r.full_name, email: r.email.trim(), role: r.role || null, cohort_id: r.cohort_id || null })) };
      const { data } = await axios.post(`${API}/admin/users/bulk`, body, { headers });
      setResult(data);
      toast.success(`${data.created} account(s) created`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk add failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-add-users-dialog">
        <DialogHeader>
          <DialogTitle>Bulk add users</DialogTitle>
          <DialogDescription className="text-zinc-400">Type people in, paste a list or upload a CSV. Each new user gets a sign-in email with a link to set their password.</DialogDescription>
        </DialogHeader>
        {result ? <BulkResult result={result} onAgain={() => { setResult(null); setRows([blank()]); }} onClose={() => onOpenChange(false)} />
          : (<div className="space-y-5">
            <PasteAndUpload paste={paste} setPaste={setPaste} onParse={() => { addParsed(parsePeople(paste)); setPaste(''); }} fileRef={fileRef} onFile={onFile} />
            <PeopleRows rows={rows} setRow={setRow} cohorts={cohorts}
              onAdd={() => setRows((p) => [...p, blank()])} onRemove={(i) => setRows((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : [blank()]))} />
            <BatchOptions batch={batch} setBatch={setBatch} cohorts={cohorts} programmes={programmes} />
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-zinc-800">
              <p className="text-xs text-zinc-500" data-testid="bulk-add-count">{filled.length} {filled.length === 1 ? 'person' : 'people'} ready{invalid ? ` · ${invalid} with an invalid email` : ''}</p>
              <button onClick={submit} disabled={busy || !filled.length || invalid > 0} className="btn-gold px-5 py-2 text-sm disabled:opacity-50" data-testid="bulk-add-submit-btn">
                {busy ? 'Creating accounts...' : `Create ${filled.length} account(s)`}
              </button>
            </div>
          </div>)}
      </DialogContent>
    </Dialog>
  );
}

function PasteAndUpload({ paste, setPaste, onParse, fileRef, onFile }) {
  return (
    <div className="grid md:grid-cols-3 gap-3">
      <div className="md:col-span-2">
        <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3} data-testid="bulk-paste-input"
          placeholder={'Paste one person per line, e.g.\nThandi Mokoena, thandi@company.co.za\nor copy columns straight from Excel'}
          className="w-full bg-zinc-950 border border-zinc-700 rounded-md text-sm text-white p-2 focus:outline-none focus:border-navigate-gold" />
        <button type="button" onClick={onParse} disabled={!paste.trim()} className="mt-1 inline-flex items-center gap-1.5 text-xs text-navigate-gold hover:underline disabled:opacity-40" data-testid="bulk-paste-add-btn">
          <ClipboardPaste className="h-3.5 w-3.5" />Add pasted people
        </button>
      </div>
      <div className="rounded-lg border border-dashed border-zinc-700 p-3 flex flex-col items-center justify-center text-center gap-2">
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={onFile} data-testid="bulk-csv-input" />
        <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm" data-testid="bulk-csv-upload-btn">
          <Upload className="h-4 w-4" />Upload CSV
        </button>
        <p className="text-[11px] text-zinc-500">Columns: name, email, role (optional), cohort (optional)</p>
      </div>
    </div>
  );
}

function PeopleRows({ rows, setRow, cohorts, onAdd, onRemove }) {
  return (
    <div>
      <div className="grid grid-cols-[1fr_1.2fr_130px_150px_32px] gap-2 text-[11px] uppercase text-zinc-500 px-1 mb-1">
        <span>Full name</span><span>Email</span><span>Role</span><span>Cohort</span><span />
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="bulk-rows">
        {rows.map((r, i) => {
          const bad = r.email && !isEmail(r.email.trim());
          return (
            <div key={i} className="grid grid-cols-[1fr_1.2fr_130px_150px_32px] gap-2" data-testid={`bulk-row-${i}`}>
              <input className={inp} value={r.full_name} onChange={(e) => setRow(i, 'full_name', e.target.value)} placeholder="Name" data-testid={`bulk-row-name-${i}`} />
              <input className={`${inp} ${bad ? 'border-rose-500' : ''}`} value={r.email} onChange={(e) => setRow(i, 'email', e.target.value)} placeholder="email@company.co.za" data-testid={`bulk-row-email-${i}`} />
              <select className={inp} value={r.role} onChange={(e) => setRow(i, 'role', e.target.value)} data-testid={`bulk-row-role-${i}`}>
                <option value="">Batch role</option>
                {ROLES.map((x) => <option key={x} value={x}>{x.replace('_', ' ')}</option>)}
              </select>
              <select className={inp} value={r.cohort_id} onChange={(e) => setRow(i, 'cohort_id', e.target.value)} data-testid={`bulk-row-cohort-${i}`}>
                <option value="">Batch cohort</option>
                {cohorts.map((c) => <option key={c.cohort_id} value={c.cohort_id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => onRemove(i)} className="h-9 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10" data-testid={`bulk-row-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={onAdd} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:border-zinc-500" data-testid="bulk-add-row-btn">
        <Plus className="h-4 w-4" />Add another person
      </button>
    </div>
  );
}

function BatchOptions({ batch, setBatch, cohorts, programmes }) {
  const toggle = (id) => setBatch((b) => ({ ...b, programme_ids: b.programme_ids.includes(id) ? b.programme_ids.filter((x) => x !== id) : [...b.programme_ids, id] }));
  return (
    <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 p-4 space-y-3" data-testid="bulk-batch-options">
      <p className="text-sm font-semibold text-white">Apply to everyone in this batch</p>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-xs text-zinc-400">Role
          <select className={`${inp} mt-1`} value={batch.role} onChange={(e) => setBatch({ ...batch, role: e.target.value })} data-testid="bulk-batch-role">
            {ROLES.map((x) => <option key={x} value={x}>{x.replace('_', ' ')}</option>)}
          </select>
        </label>
        <label className="text-xs text-zinc-400">Cohort
          <select className={`${inp} mt-1`} value={batch.cohort_id} onChange={(e) => setBatch({ ...batch, cohort_id: e.target.value })} data-testid="bulk-batch-cohort">
            <option value="">No cohort</option>
            {cohorts.map((c) => <option key={c.cohort_id} value={c.cohort_id}>{c.name}</option>)}
          </select>
        </label>
      </div>
      <div>
        <p className="text-xs text-zinc-400 mb-1.5">Enrol in programmes</p>
        <div className="flex flex-wrap gap-2">
          {programmes.length === 0 && <span className="text-xs text-zinc-600">No programmes yet</span>}
          {programmes.map((p) => {
            const on = batch.programme_ids.includes(p.programme_id);
            return (
              <button type="button" key={p.programme_id} onClick={() => toggle(p.programme_id)} data-testid={`bulk-batch-programme-${p.programme_id}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-navigate-gold text-black border-navigate-gold' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                {p.programme_name || p.title}
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 flex items-center gap-1"><Info className="h-3 w-3" />A role or cohort picked on a row overrides the batch setting for that person.</p>
    </div>
  );
}

function BulkResult({ result, onAgain, onClose }) {
  const copyLinks = () => {
    const txt = result.results.filter((r) => r.invite_link).map((r) => `${r.full_name} <${r.email}>: ${r.invite_link}`).join('\n');
    navigator.clipboard?.writeText(txt).then(() => toast.success('Links copied'), () => toast.error('Copy failed'));
  };
  const tone = { created: 'text-emerald-300', exists: 'text-amber-300', error: 'text-rose-300' };
  return (
    <div className="space-y-4" data-testid="bulk-add-result">
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 text-emerald-300"><CheckCircle2 className="h-4 w-4" />{result.created} created</span>
        {result.existing > 0 && <span className="text-amber-300">{result.existing} already had accounts</span>}
        {result.errors > 0 && <span className="inline-flex items-center gap-1.5 text-rose-300"><AlertTriangle className="h-4 w-4" />{result.errors} not added</span>}
      </div>
      <p className="text-xs text-zinc-400">
        {result.email_queued ? 'Sign-in emails have been queued. Delivery depends on your email settings — if anyone doesn\'t receive one, copy the links below and share them directly.'
          : 'Email isn\'t set up, so no emails were sent. Copy the set-password links below and share them with each person.'}
      </p>
      <div className="max-h-72 overflow-y-auto rounded-lg border border-zinc-800 divide-y divide-zinc-800">
        {result.results.map((r) => (
          <div key={r.row} className="px-3 py-2 text-sm flex items-center justify-between gap-3" data-testid={`bulk-result-${r.email || r.row}`}>
            <div className="min-w-0"><p className="text-white truncate">{r.full_name} <span className="text-zinc-500">{r.email}</span></p>
              {r.detail && <p className="text-xs text-zinc-500">{r.detail}</p>}</div>
            <span className={`text-xs shrink-0 ${tone[r.status]}`}>{r.status === 'exists' ? 'Already exists' : r.status === 'created' ? 'Created' : 'Not added'}{r.programmes_enrolled ? ` · enrolled in ${r.programmes_enrolled} programme(s)` : ''}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        {result.results.some((r) => r.invite_link) && <button onClick={copyLinks} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm" data-testid="bulk-copy-links-btn"><Copy className="h-4 w-4" />Copy set-password links</button>}
        <button onClick={onAgain} className="px-3 py-2 rounded-md border border-zinc-700 text-sm" data-testid="bulk-add-more-btn">Add more</button>
        <button onClick={onClose} className="btn-gold px-4 py-2 text-sm" data-testid="bulk-add-done-btn">Done</button>
      </div>
    </div>
  );
}
